import { useState, useRef, useCallback, useEffect } from 'react'
import { Globe, ExternalLink, Info, Edit, Save, RefreshCw } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import { Toggle, Checkbox, TextField, Button, IconButton, Autocomplete } from '@anyemp/ui-kit'
import { talents, libraries } from '../../services/apiService'
import toast from 'react-hot-toast'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

/**
 * Public Profile section — used in both TalentDetail (standalone mode)
 * and TalentForm (formMode).
 *
 * Standalone mode props: talent, talentId, hasPermission, setTalent
 * Form mode props: formMode, publicSettings, setPublicSettings, firstName, lastName, currentTalentId
 */
const PublicProfileSection = ({
  // Standalone mode (TalentDetail)
  talent,
  talentId,
  hasPermission,
  setTalent,
  // Form mode (TalentForm)
  formMode = false,
  publicSettings: controlled,
  setPublicSettings: setControlled,
  firstName: externalFirstName,
  lastName: externalLastName,
  currentTalentId,
}) => {
  // Standalone-only state
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)
  const [slugError, setSlugError] = useState(null)
  const [visibilityLoading, setVisibilityLoading] = useState(false)
  const [profileActiveLoading, setProfileActiveLoading] = useState(false)

  // Shared: rate options (loaded on open)
  const [rateOptions, setRateOptions] = useState([])

  // Generation state
  const [isGeneratingSlug, setIsGeneratingSlug] = useState(false)
  const [isGeneratingShortName, setIsGeneratingShortName] = useState(false)
  // Latest request number per field: a response to a stale request
  // (the name has changed since) never reaches the form
  const generationRequestRef = useRef({ slug: 0, short_name: 0 })

  // In form mode state is controlled externally; in standalone mode it's internal formData
  const publicData = formMode ? controlled : formData
  const setPublicData = formMode ? setControlled : setFormData

  // Names used for generation
  const firstName = formMode ? externalFirstName : talent?.first_name
  const lastName = formMode ? externalLastName : talent?.last_name
  const effectiveTalentId = formMode ? currentTalentId : talentId

  // ---------------------------------------------------------------------------
  // Slug / short name generation — formulas and uniqueness are handled by the backend
  // ---------------------------------------------------------------------------
  const generateIdentifiers = useCallback(async (fn, ln, fields) => {
    const requestIds = {}
    fields.forEach(field => { requestIds[field] = ++generationRequestRef.current[field] })
    const isFresh = (field) => generationRequestRef.current[field] === requestIds[field]

    const setGenerating = (value) => {
      if (fields.includes('slug') && isFresh('slug')) setIsGeneratingSlug(value)
      if (fields.includes('short_name') && isFresh('short_name')) setIsGeneratingShortName(value)
    }
    const apply = (values) => setPublicData(prev => {
      const next = { ...prev }
      fields.forEach(field => { if (isFresh(field)) next[field] = values[field] || '' })
      return next
    })

    if (!fn || !ln) {
      apply({})
      return
    }

    setGenerating(true)
    try {
      const res = await talents.generatePublicIdentifiers(fn, ln, effectiveTalentId)
      if (res?.success) apply(res.data || {})
    } catch (err) {
      console.error('Error generating public identifiers:', err)
    } finally {
      setGenerating(false)
    }
  }, [effectiveTalentId]) // eslint-disable-line react-hooks/exhaustive-deps

  // In form mode, load rates on mount
  useEffect(() => {
    if (formMode) loadRates()
  }, [formMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Form mode: auto-generate when first/last name changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!formMode || !externalFirstName?.trim() || !externalLastName?.trim()) return
    const t = setTimeout(
      () => generateIdentifiers(externalFirstName.trim(), externalLastName.trim(), ['slug', 'short_name']),
      500
    )
    return () => clearTimeout(t)
  }, [formMode, externalFirstName, externalLastName, generateIdentifiers])

  // ---------------------------------------------------------------------------
  // Rate options loader
  // ---------------------------------------------------------------------------
  const loadRates = async () => {
    if (rateOptions.length > 0) return
    try {
      const res = await libraries.getRates()
      const list = Array.isArray(res?.data) ? res.data : (res?.data?.data || res || [])
      setRateOptions(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Error loading rates:', err)
    }
  }

  // ---------------------------------------------------------------------------
  // Standalone mode: open edit
  // ---------------------------------------------------------------------------
  const publicSettings = talent?.publicSettings

  const openEdit = async () => {
    setFormData({
      slug: publicSettings?.slug || '',
      seo_title: publicSettings?.seo_title || '',
      seo_description: publicSettings?.seo_description || '',
      short_name: publicSettings?.short_name || '',
      slogan: publicSettings?.slogan || '',
      rate_id: publicSettings?.rate_id || '',
      is_pinned: !!publicSettings?.is_pinned,
    })
    setSlugError(null)
    setIsEditing(true)
    loadRates()
  }

  // ---------------------------------------------------------------------------
  // Slug check on blur / before save. The backend owns the slug format: it
  // returns the slug as it will be stored and tells whether it is free
  // ---------------------------------------------------------------------------
  const checkSlug = async (slug) => {
    if (!slug) { setSlugError(null); return true }
    try {
      const res = await talents.checkSlugUniqueness(slug, effectiveTalentId)
      const stored = res?.data?.slug
      if (stored && stored !== slug) setPublicData(prev => ({ ...prev, slug: stored }))
      if (res?.data?.exists || res?.exists || (!res?.unique && res?.success !== undefined)) {
        setSlugError('This slug is already taken')
        return false
      }
      setSlugError(null)
      return true
    } catch (err) {
      // 400: nothing is left of the value once it is cleaned up
      if (err?.response?.status === 400) {
        setSlugError('Use Latin letters, digits and hyphens')
        return false
      }
      setSlugError(null)
      return true
    }
  }

  // ---------------------------------------------------------------------------
  // Standalone mode: save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const slugOk = await checkSlug(formData.slug)
    if (!slugOk) return
    try {
      setSaving(true)
      await talents.upsertPublicProfile(talentId, {
        slug: formData.slug || '',
        seo_title: formData.seo_title || '',
        seo_description: formData.seo_description || '',
        short_name: formData.short_name || '',
        slogan: formData.slogan || null,
        rate_id: formData.rate_id || null,
        is_pinned: !!formData.is_pinned,
      })
      const response = await talents.getTalentByIdWithRelations(talentId)
      if (response?.data) setTalent(response.data)
      setIsEditing(false)
      toast.success('Public profile saved', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error saving public profile:', err)
      toast.error('Failed to save public profile', { duration: 2000, position: 'bottom-right' })
    } finally {
      setSaving(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Standalone: toggles public_visibility / public_profile_active
  // ---------------------------------------------------------------------------
  const handleVisibilityToggle = async (e) => {
    const val = e.target.checked
    setVisibilityLoading(true)
    try {
      await talents.updateTalentFlags(talentId, { public_visibility: val })
      setTalent(prev => ({ ...prev, public_visibility: val }))
      toast.success(val ? 'Profile is now public' : 'Profile is now private', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error updating visibility:', err)
      toast.error('Failed to update visibility', { duration: 2000, position: 'bottom-right' })
    } finally {
      setVisibilityLoading(false)
    }
  }

  const handleProfileActiveToggle = async (e) => {
    const val = e.target.checked
    setProfileActiveLoading(true)
    try {
      await talents.updateTalentFlags(talentId, { public_profile_active: val })
      setTalent(prev => ({ ...prev, public_profile_active: val }))
      toast.success(val ? 'Profile activated' : 'Profile deactivated', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      console.error('Error updating profile active:', err)
      toast.error('Failed to update profile status', { duration: 2000, position: 'bottom-right' })
    } finally {
      setProfileActiveLoading(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Shared: form fields
  // ---------------------------------------------------------------------------
  const rateOptionsMapped = rateOptions.map(r => ({
    value: String(r.id),
    label: r.hours ? `${r.name} (${r.hours}h, ${r.value})` : r.name
  }))

  const renderFields = () => (
    <div className="grid grid-cols-1 gap-4">
      {/* Slug + Short Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* URL Slug */}
        <div>
          <TextField
            label={`URL Slug${isGeneratingSlug ? ' ⏳' : ''}`}
            required={!formMode}
            fullWidth
            size="small"
            type="text"
            value={publicData?.slug || ''}
            onChange={e => {
              const val = e.target.value
              setPublicData(prev => ({ ...prev, slug: val }))
              setSlugError(null)
            }}
            onBlur={e => checkSlug(e.target.value)}
            disabled={isGeneratingSlug}
            error={!!slugError}
            helperText={slugError || 'Friendly URL for the public profile. Automatically generated from name.'}
            placeholder="john-d"
            endAdornment={(
              <IconButton
                type="button"
                variant="ghost"
                color="neutral"
                size="small"
                aria-label="Generate slug from name"
                title="Generate from name"
                disabled={isGeneratingSlug || !firstName || !lastName}
                onClick={() => generateIdentifiers(firstName?.trim(), lastName?.trim(), ['slug'])}
                icon={<RefreshCw className="h-3.5 w-3.5" />}
              />
            )}
          />
        </div>

        {/* Short Name */}
        <div>
          <TextField
            label={`Short Name${isGeneratingShortName ? ' ⏳' : ''}`}
            fullWidth
            size="small"
            type="text"
            value={publicData?.short_name || ''}
            onChange={e => setPublicData(prev => ({ ...prev, short_name: e.target.value }))}
            disabled={isGeneratingShortName}
            placeholder="John D"
            maxLength={100}
            helperText={'Short display name (e.g. "John D"). Auto-generated from name.'}
            endAdornment={(
              <IconButton
                type="button"
                variant="ghost"
                color="neutral"
                size="small"
                aria-label="Generate short name from name"
                title="Generate from name"
                disabled={isGeneratingShortName || !firstName || !lastName}
                onClick={() => generateIdentifiers(firstName?.trim(), lastName?.trim(), ['short_name'])}
                icon={<RefreshCw className="h-3.5 w-3.5" />}
              />
            )}
          />
        </div>
      </div>

      {/* SEO Title */}
      <div>
        <TextField
          label="SEO Title"
          fullWidth
          size="small"
          type="text"
          value={publicData?.seo_title || ''}
          onChange={e => setPublicData(prev => ({ ...prev, seo_title: e.target.value }))}
          placeholder="John Doe - Frontend Developer | Tech Specialist"
          maxLength={200}
          helperText="Title for search engines (up to 200 characters)"
        />
      </div>

      {/* SEO Description */}
      <div>
        <TextField
          label="SEO Description"
          multiline
          rows={3}
          fullWidth
          size="small"
          value={publicData?.seo_description || ''}
          onChange={e => setPublicData(prev => ({ ...prev, seo_description: e.target.value }))}
          placeholder="Experienced developer with expertise in React and TypeScript..."
          helperText="Description for search engines and social media"
        />
      </div>

      {/* Rate */}
      <div>
        <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">Rate</label>
        <Autocomplete
          clearable
          fullWidth
          options={rateOptionsMapped}
          value={publicData?.rate_id != null ? String(publicData.rate_id) : ''}
          onChange={val => setPublicData(prev => ({ ...prev, rate_id: val ? Number(val) : '' }))}
          placeholder="Select rate..."
          disabled={rateOptions.length === 0}
        />
        <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Rate for public display (optional)</p>
      </div>

      {/* Slogan */}
      <div>
        <TextField
          label="Slogan"
          fullWidth
          size="small"
          type="text"
          value={publicData?.slogan || ''}
          onChange={e => setPublicData(prev => ({ ...prev, slogan: e.target.value || null }))}
          placeholder="Short tagline for public profile..."
          maxLength={255}
          helperText="Slogan for public display (optional)"
        />
      </div>

      {/* Pinned */}
      <div>
        <Checkbox
          checked={!!publicData?.is_pinned}
          onChange={(checked) => setPublicData(prev => ({ ...prev, is_pinned: checked }))}
          label="Pinned (AnyEmp)"
          color="primary"
        />
        <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Pin this talent in public catalog</p>
      </div>
    </div>
  )

  // ---------------------------------------------------------------------------
  // Form mode — just the fields
  // ---------------------------------------------------------------------------
  if (formMode) {
    return renderFields()
  }

  // ---------------------------------------------------------------------------
  // Standalone mode — SectionBlock with read view + edit toggle
  // ---------------------------------------------------------------------------
  return (
    <SectionBlock id="public-profile">
      <SectionHeader
        icon={Globe}
        title="Public Profile"
        action={!isEditing && hasPermission('talent-service', `${talent.talent_type}_public_profile`, 'update') ? (
          <Button type="button" color="primary" size="small" onClick={openEdit}>
            <Edit className="h-3.5 w-3.5 mr-1.5 inline-block align-[-2px]" />
            {publicSettings ? 'Edit' : 'Add'}
          </Button>
        ) : null}
      />

      {hasPermission('talent-service', `${talent.talent_type}_public_profile`, 'update') && (
        <div className="flex items-center gap-6 mb-4 pb-4 border-b border-light-border-primary dark:border-dark-border-primary">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Visibility</span>
            <div className="flex items-center gap-2">
              <Toggle
                checked={!!talent.public_visibility}
                isDisabled={visibilityLoading}
                onChange={(checked, e) => handleVisibilityToggle(e)}
                color="primary"
                label={talent.public_visibility ? 'Public' : 'Private'}
              />
              {visibilityLoading && <div className="animate-spin h-3.5 w-3.5 border-2 border-blue-500 border-t-transparent rounded-full" />}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Profile Active</span>
            <div className="flex items-center gap-2">
              <Toggle
                checked={!!talent.public_profile_active}
                isDisabled={profileActiveLoading || !talent.public_visibility}
                onChange={(checked, e) => handleProfileActiveToggle(e)}
                color="success"
                label={talent.public_profile_active ? 'Active' : 'Inactive'}
              />
              {profileActiveLoading && <div className="animate-spin h-3.5 w-3.5 border-2 border-green-500 border-t-transparent rounded-full" />}
            </div>
          </div>
        </div>
      )}

      {isEditing ? (
        <div className="space-y-4">
          {renderFields()}
          <div className="flex gap-3 justify-end pt-2">
            <Button
              type="button"
              variant="outlined"
              color="neutral"
              size="small"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              <XIcon className="h-4 w-4 mr-1.5 inline-block align-[-2px]" />
              Cancel
            </Button>
            <Button
              type="button"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={saving || !!slugError || isGeneratingSlug || isGeneratingShortName}
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block align-[-3px]" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1.5 inline-block align-[-2px]" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      ) : publicSettings ? (
        <div className="space-y-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          {publicSettings.seo_title && (
            <div>
              <label className="block text-xs font-medium text-blue-800 dark:text-blue-400 mb-1">SEO Title</label>
              <p className="text-sm text-blue-900 dark:text-blue-300 font-medium">{publicSettings.seo_title}</p>
            </div>
          )}
          {publicSettings.seo_description && (
            <div>
              <label className="block text-xs font-medium text-blue-800 dark:text-blue-400 mb-1">SEO Description</label>
              <p className="text-sm text-blue-900 dark:text-blue-300 leading-relaxed">{publicSettings.seo_description}</p>
            </div>
          )}
          {publicSettings.slug && (
            <div>
              <label className="flex items-center text-xs font-medium text-blue-800 dark:text-blue-400 mb-1 gap-1">
                URL Slug <ExternalLink className="h-3 w-3" />
              </label>
              <div className="flex items-center gap-2">
                <code className="text-sm text-blue-900 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded border border-blue-200 dark:border-blue-700">
                  {publicSettings.slug}
                </code>
                <Button
                  type="button"
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => {
                    const url = `${window.location.origin}/profile/${publicSettings.slug}`
                    navigator.clipboard.writeText(url)
                      .then(() => toast.success('Copied!'))
                      .catch(() => {
                        const ta = document.createElement('textarea')
                        ta.value = url
                        document.body.appendChild(ta)
                        ta.select()
                        document.execCommand('copy')
                        document.body.removeChild(ta)
                        toast.success('Copied!')
                      })
                  }}
                >
                  Copy Link
                </Button>
              </div>
            </div>
          )}
          {publicSettings.short_name && (
            <div>
              <label className="block text-xs font-medium text-blue-800 dark:text-blue-400 mb-1">Short Name</label>
              <p className="text-sm text-blue-900 dark:text-blue-300 font-medium">{publicSettings.short_name}</p>
            </div>
          )}
          {publicSettings.rate && (
            <div>
              <label className="block text-xs font-medium text-blue-800 dark:text-blue-400 mb-1">Rate</label>
              <span className="text-sm font-bold text-blue-900 dark:text-blue-300">{publicSettings.rate.name}</span>
            </div>
          )}
          {publicSettings.slogan && (
            <div>
              <label className="block text-xs font-medium text-blue-800 dark:text-blue-400 mb-1">Slogan</label>
              <p className="text-sm text-blue-900 dark:text-blue-300 font-medium">{publicSettings.slogan}</p>
            </div>
          )}
          {publicSettings.is_pinned && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
              Pinned (AnyEmp)
            </span>
          )}
          <div className="mt-2 p-3 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div className="text-xs text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-0.5">Public profile is active</p>
              <p>This profile is displayed in the public talent catalog.</p>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No public settings configured</p>
      )}
    </SectionBlock>
  )
}

export default PublicProfileSection
